import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Check } from 'lucide-react';
import { SERVICES } from '../../data/content';

export const Services = ({ onBook }) => {
  const [active, setActive] = useState(0);
  const current = SERVICES[active];

  return (
    <section
      id="services"
      className="relative py-24 md:py-40 bg-[#F9F7F4] overflow-hidden"
      data-testid="services-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Heading */}
        <div className="grid md:grid-cols-12 gap-8 mb-20">
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-6"
            >
              <div className="h-px w-12 bg-[#0D1B2A]" />
              <span className="text-[#0D1B2A] text-xs tracking-[0.4em] uppercase font-medium">
                Our Craft
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-serif text-[#0D1B2A] leading-[1] tracking-tight"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 5.5rem)' }}
            >
              Every space,
              <br />
              <span className="italic font-light">deserves better.</span>
            </motion.h2>
          </div>

          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-base md:text-lg text-[#0D1B2A]/70 font-light leading-relaxed max-w-lg"
            >
              From four-walled apartments to multi-floor estates, our trained
              specialists bring the same obsessive standard to every brief.
              Choose your service, we handle the rest.
            </motion.p>
          </div>
        </div>

        {/* Service tabs + preview */}
        <div className="grid md:grid-cols-12 gap-12">
          {/* Tabs */}
          <div className="md:col-span-5 flex flex-col">
            {SERVICES.map((s, idx) => (
              <button
                key={s.id}
                onClick={() => setActive(idx)}
                onMouseEnter={() => setActive(idx)}
                data-testid={`service-tab-${s.id}`}
                className={`group text-left py-6 border-t border-[#0D1B2A]/10 transition-all ${
                  idx === SERVICES.length - 1 ? 'border-b' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <span
                      className={`text-xs tabular-nums tracking-wider transition-colors ${
                        active === idx ? 'text-[#C9A84C]' : 'text-[#0D1B2A]/30'
                      }`}
                    >
                      0{idx + 1}
                    </span>
                    <div>
                      <div
                        className={`font-serif transition-colors duration-300 ${
                          active === idx ? 'text-[#0D1B2A]' : 'text-[#0D1B2A]/40 group-hover:text-[#0D1B2A]/70'
                        }`}
                        style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)' }}
                      >
                        {s.name}
                      </div>
                    </div>
                  </div>
                  <ArrowUpRight
                    className={`w-5 h-5 transition-all ${
                      active === idx
                        ? 'text-[#C9A84C] rotate-0 opacity-100'
                        : 'text-[#0D1B2A]/30 -rotate-45 opacity-0 group-hover:opacity-100'
                    }`}
                  />
                </div>
              </button>
            ))}
          </div>

          {/* Preview panel */}
          <div className="md:col-span-7">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              className="sticky top-32"
              data-testid="service-preview"
            >
              <div className="relative aspect-[4/5] md:aspect-[5/6] overflow-hidden bg-[#0D1B2A]">
                <motion.img
                  key={current.image}
                  src={current.image}
                  alt={current.name}
                  className="w-full h-full object-cover"
                  initial={{ scale: 1.1, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ duration: 0.8 }}
                />
                {/* Strong gradient for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D1B2A]/95 via-[#0D1B2A]/55 to-[#0D1B2A]/10 md:from-[#0D1B2A]/90 md:via-[#0D1B2A]/25 md:to-transparent" />

                <div className="absolute inset-x-0 bottom-0 p-6 md:p-10 text-white">
                  <div className="text-[#C9A84C] text-xs tracking-[0.3em] uppercase mb-3">
                    {current.short}
                  </div>
                  <p className="font-serif text-xl md:text-2xl lg:text-3xl leading-[1.2] mb-6 max-w-md text-white">
                    {current.desc}
                  </p>
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {current.features.map((f) => (
                      <div key={f} className="flex items-center gap-2 text-sm text-white/90">
                        <Check className="w-3.5 h-3.5 text-[#C9A84C] flex-shrink-0" />
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={onBook}
                    className="group inline-flex items-center gap-2 bg-[#C9A84C] text-[#0D1B2A] hover:bg-white border border-[#C9A84C] px-6 py-3 text-xs tracking-[0.25em] uppercase font-semibold transition-colors"
                    data-testid={`service-book-${current.id}`}
                  >
                    Book this service
                    <ArrowUpRight className="w-3.5 h-3.5 group-hover:rotate-45 transition-transform" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
